import { useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Crop, ImagePlus, Loader2, Star, X } from "lucide-react";
import { PhotoFocusEditor, focusStyle } from "@/components/profile/PhotoFocusEditor";
import { toast } from "sonner";
import { uploadImage } from "@/components/ImageUploader";
import { imageUrl } from "@/lib/api";
import { cn } from "@/lib/utils";

export const MIN_PHOTOS = 3;
export const MAX_PHOTOS = 8;

const move = (list, from, to) => {
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

const Tile = ({ path, index, total, onChange, list, dragFrom }) => {
  const iconBtn = "flex h-8 w-8 items-center justify-center rounded-full bg-background/85 transition-colors hover:bg-background disabled:opacity-30";
  return (
    <li draggable onDragStart={() => { dragFrom.current = index; }} onDragOver={(e) => e.preventDefault()}
      onDrop={() => { if (dragFrom.current !== null && dragFrom.current !== index) onChange(move(list, dragFrom.current, index)); dragFrom.current = null; }}
      className={cn("group relative aspect-[3/4] cursor-grab overflow-hidden rounded-xl border bg-card", index === 0 ? "border-primary" : "border-border")}
      data-testid={`photo-tile-${index}`}>
      <img src={imageUrl(path)} alt={`Profile photo ${index + 1}`} className="img-cover pointer-events-none" />
      {index === 0 && <span className="absolute left-2 top-2 rounded-full bg-primary px-2 py-0.5 text-xs font-semibold text-primary-foreground" data-testid="photo-main-badge">Main</span>}
      <button type="button" aria-label={`Remove photo ${index + 1}`} onClick={() => onChange(list.filter((p) => p !== path))} className={cn(iconBtn, "absolute right-2 top-2")} data-testid={`photo-remove-${index}`}><X className="h-4 w-4" /></button>
      <div className="absolute inset-x-2 bottom-2 flex items-center justify-between">
        <button type="button" aria-label={`Move photo ${index + 1} left`} disabled={index === 0} onClick={() => onChange(move(list, index, index - 1))} className={iconBtn} data-testid={`photo-left-${index}`}><ChevronLeft className="h-4 w-4" /></button>
        <button type="button" aria-label={index === 0 ? "Main photo" : `Make photo ${index + 1} your main photo`} aria-pressed={index === 0} disabled={index === 0}
          onClick={() => onChange(move(list, index, 0))} className={cn(iconBtn, index === 0 && "text-primary disabled:opacity-100")} data-testid={`photo-star-${index}`}>
          <Star className={cn("h-4 w-4", index === 0 && "fill-current")} />
        </button>
        <button type="button" aria-label={`Move photo ${index + 1} right`} disabled={index === total - 1} onClick={() => onChange(move(list, index, index + 1))} className={iconBtn} data-testid={`photo-right-${index}`}><ChevronRight className="h-4 w-4" /></button>
      </div>
    </li>
  );
};

const MainPreview = ({ path, focus, onFocus }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mb-4 flex items-center gap-4" data-testid="photo-main-preview">
      <span className="h-20 w-20 shrink-0 overflow-hidden rounded-full border-2 border-primary/60 bg-secondary">
        <img src={imageUrl(path)} alt="Your profile picture" className="img-cover" style={focus?.path === path ? focusStyle(focus) : undefined} data-testid="photo-main-circle" />
      </span>
      <div>
        <p className="text-sm font-semibold">Profile picture</p>
        <p className="text-xs text-muted-foreground">This is how you appear in circles across the club.</p>
        <button type="button" className="btn-outline mt-2 h-9 px-3 text-xs" onClick={() => setOpen(true)} data-testid="photo-adjust-button"><Crop className="h-3.5 w-3.5" /> Adjust</button>
      </div>
      {open && <PhotoFocusEditor path={path} focus={focus} open={open} onOpenChange={setOpen} onSave={onFocus} />}
    </div>
  );
};

export const PhotoManager = ({ value, onChange, focus, onFocus }) => {
  const input = useRef(null);
  const dragFrom = useRef(null);
  const [busy, setBusy] = useState(false);
  const add = async (files) => {
    const list = Array.from(files || []).slice(0, MAX_PHOTOS - value.length);
    setBusy(true);
    const added = [];
    for (const f of list) {
      if (f.size > 8 * 1024 * 1024) { toast.error(`${f.name} is larger than 8MB`); continue; }
      try { added.push(await uploadImage(f)); } catch (e) { toast.error(`${f.name}: couldn't upload`); }
    }
    onChange([...value, ...added]);
    setBusy(false);
    if (input.current) input.current.value = "";
  };
  return (
    <div data-testid="photo-manager">
      <div className="mb-3 flex items-baseline justify-between">
        <p className="field-label mb-0">Photos</p>
        <p className={cn("text-sm", value.length < MIN_PHOTOS ? "text-[hsl(var(--gold))]" : "text-muted-foreground")} data-testid="photo-count">{value.length} of {MAX_PHOTOS} · at least {MIN_PHOTOS}</p>
      </div>
      {value[0] && onFocus && <MainPreview path={value[0]} focus={focus} onFocus={onFocus} />}
      <ul className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {value.map((p, i) => <Tile key={p} path={p} index={i} total={value.length} list={value} onChange={onChange} dragFrom={dragFrom} />)}
        {value.length < MAX_PHOTOS && (
          <li><button type="button" onClick={() => input.current?.click()} disabled={busy} data-testid="photo-add-button"
            className="flex aspect-[3/4] w-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border text-sm text-muted-foreground transition-colors hover:border-primary hover:text-foreground">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}{busy ? "Uploading" : "Add photos"}
          </button></li>
        )}
      </ul>
      <p className="mt-3 text-xs text-muted-foreground">Drag or use the arrows to reorder. Tap the star to choose your main photo.</p>
      <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={(e) => add(e.target.files)} data-testid="photo-input" />
    </div>
  );
};
